/**
 * RecordReceiptDialog — modal to record a customer payment against the
 * current invoice. Triggered from the invoice detail page.
 *
 * V1 scope: single-invoice allocation (the modal is always opened from
 * one invoice's context). Multi-invoice allocation lands in a separate
 * /collections "Record receipt" flow when needed.
 *
 * The form does the allocation arithmetic for the operator: if TDS is
 * deducted, the gross applied to the invoice = amount + tds. The wire
 * invariant `Σ allocations.paise == amount + tds` is satisfied by
 * construction here. (PR-C.)
 *
 * TDS pre-fill: when the customer has `isTdsApplicable=true`, the modal
 * starts with a TDS line at the section's typical rate (10% for 194J,
 * 1% for 194C, 0.1% for 194Q) — operators override per receipt.
 */
import { useEffect, useMemo, useState } from "react";
import { PlusIcon, Trash2Icon } from "lucide-react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";

const MODE_OPTIONS = [
  { value: "upi", label: "UPI" },
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
] as const;

type Mode = (typeof MODE_OPTIONS)[number]["value"];

const SETTLEMENT_TYPE_OPTIONS = [
  { label: "Foreign bank charge", kind: "foreign_bank_charge", direction: "deduction" },
  { label: "Indian bank charge", kind: "indian_bank_charge", direction: "deduction" },
  { label: "GST on bank charge", kind: "bank_charge_gst", direction: "deduction" },
  { label: "Round off", kind: "round_off", direction: "deduction" },
  { label: "Write off", kind: "write_off", direction: "deduction" },
  { label: "Other deduction", kind: "other_deduction", direction: "deduction" },
  { label: "Other addition", kind: "other_addition", direction: "addition" },
] as const;

type SettlementKind = (typeof SETTLEMENT_TYPE_OPTIONS)[number]["kind"];
type SettlementDirection = "deduction" | "addition";

interface SettlementLineState {
  id: string;
  label: string;
  kind: SettlementKind;
  direction: SettlementDirection;
  amount: string;
  currencyCode: string;
  fxRate: string;
  note: string;
}

/** Section → typical deduction rate in basis points. Operator overrides. */
const TDS_DEFAULT_BPS: Record<string, number> = {
  "194C": 200, // 2% (companies; 1% for individuals — operator picks)
  "194J": 1000, // 10% professional services
  "194Q": 10, // 0.1% goods over threshold
};

export interface RecordReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: {
    id: string;
    docNumber: string | null;
    customerId: string;
    totalPaise: string;
    /** Total received against this invoice so far (paise). Used to compute outstanding. */
    receivedSoFarPaise: string;
    currencyCode: string;
    foreignAmountMinor: string | null;
    fxRateToInrPaise: string | null;
  };
  customer: {
    id: string;
    name: string;
    isTdsApplicable: boolean;
    tdsSection: string | null;
  };
  /** Called after a successful record. Parent invalidates queries. */
  onRecorded: () => void;
}

export function RecordReceiptDialog({
  open,
  onOpenChange,
  invoice,
  customer,
  onRecorded,
}: RecordReceiptDialogProps) {
  const outstandingPaise = BigInt(invoice.totalPaise) - BigInt(invoice.receivedSoFarPaise);
  const [receivedOn, setReceivedOn] = useState(() => todayIso());
  const [mode, setMode] = useState<Mode>("upi");
  const [reference, setReference] = useState("");
  const [tdsRupees, setTdsRupees] = useState("");
  const [cashRupees, setCashRupees] = useState("");
  const [foreignPaidAmount, setForeignPaidAmount] = useState("");
  const [receiptFxRate, setReceiptFxRate] = useState("");
  const [settlementLines, setSettlementLines] = useState<SettlementLineState[]>([]);
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const isForeignInvoice = invoice.currencyCode !== "INR";

  // Accounts the money can land in — bank / cash only (you don't receive a
  // customer payment onto a credit card). Enabled only while open.
  const accountsQ = trpc.bankAccounts.list.useQuery({}, { enabled: open });
  const accounts = (accountsQ.data?.rows ?? []).filter((a) => a.type !== "credit_card");

  // Pre-fill the applied amount with the outstanding balance + TDS suggestion
  // when the dialog opens. The operator can edit either before submit.
  useEffect(() => {
    if (!open) return;
    const defaultForeignAmount = invoice.foreignAmountMinor
      ? minorToMajor(BigInt(invoice.foreignAmountMinor))
      : "";
    const defaultReceiptRate = invoice.fxRateToInrPaise
      ? minorToMajor(BigInt(invoice.fxRateToInrPaise))
      : "";
    setForeignPaidAmount(defaultForeignAmount);
    setReceiptFxRate(defaultReceiptRate);
    if (customer.isTdsApplicable && customer.tdsSection) {
      const bps = TDS_DEFAULT_BPS[customer.tdsSection] ?? 0;
      // TDS = applied × bps / 10000. Computed against the gross applied.
      const tdsPaiseEstimate = (outstandingPaise * BigInt(bps)) / 10000n;
      setTdsRupees((tdsPaiseEstimate / 100n).toString());
      setCashRupees(((outstandingPaise - tdsPaiseEstimate) / 100n).toString());
    } else {
      setTdsRupees("");
      setCashRupees((outstandingPaise / 100n).toString());
    }
    setSettlementLines([]);
    setReference("");
    setReceivedOn(todayIso());
    // Only re-run when the dialog opens fresh; updates while open are
    // operator-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer.isTdsApplicable, customer.tdsSection, invoice.id]);

  // Default the "paid into" account once accounts load (primary bank first).
  useEffect(() => {
    if (!open || accounts.length === 0) return;
    const preferred = accounts.find((a) => a.type === "bank") ?? accounts[0];
    setBankAccountId(preferred?.id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accounts.length]);

  const tdsPaise = useMemo(() => moneyTextToMinor(tdsRupees), [tdsRupees]);
  const enteredCashPaise = useMemo(() => moneyTextToMinor(cashRupees), [cashRupees]);
  const foreignPaidMinor = useMemo(() => moneyTextToMinor(foreignPaidAmount), [foreignPaidAmount]);
  const receiptFxRatePaise = useMemo(() => moneyTextToMinor(receiptFxRate), [receiptFxRate]);
  const foreignGrossPaise =
    isForeignInvoice && foreignPaidMinor > 0n && receiptFxRatePaise > 0n
      ? (foreignPaidMinor * receiptFxRatePaise) / 100n
      : null;
  const parsedSettlementLines = useMemo(
    () =>
      parseSettlementLines(settlementLines, {
        defaultFxRate: receiptFxRate,
        percentBasePaise: foreignGrossPaise ?? enteredCashPaise + tdsPaise,
      }),
    [enteredCashPaise, foreignGrossPaise, receiptFxRate, settlementLines, tdsPaise],
  );
  const settlementDeductionPaise = parsedSettlementLines.reduce(
    (acc, line) => (line.direction === "deduction" ? acc + line.amountPaise : acc),
    0n,
  );
  const settlementAdditionPaise = parsedSettlementLines.reduce(
    (acc, line) => (line.direction === "addition" ? acc + line.amountPaise : acc),
    0n,
  );
  const settlementNetPaise = settlementDeductionPaise - settlementAdditionPaise;
  const cashPaise = isForeignInvoice
    ? maxBigInt((foreignGrossPaise ?? 0n) - tdsPaise - settlementNetPaise, 0n)
    : enteredCashPaise;
  const grossSettlementPaise = cashPaise + tdsPaise + settlementNetPaise;
  const invoiceForeignMinor = invoice.foreignAmountMinor ? BigInt(invoice.foreignAmountMinor) : 0n;
  const foreignAppliedPaise =
    isForeignInvoice && invoiceForeignMinor > 0n && foreignPaidMinor > 0n
      ? (BigInt(invoice.totalPaise) * foreignPaidMinor) / invoiceForeignMinor
      : outstandingPaise;
  const appliedPaise = isForeignInvoice
    ? minBigInt(foreignAppliedPaise, outstandingPaise)
    : grossSettlementPaise;
  const fxGainLossPaise = grossSettlementPaise - appliedPaise;

  // Validation
  const validationError = useMemo(() => {
    if (tdsPaise < 0n) return "TDS cannot be negative.";
    if (isForeignInvoice && foreignPaidMinor <= 0n) return "Customer paid amount is required.";
    if (isForeignInvoice && receiptFxRatePaise <= 0n) return "Realization rate is required.";
    if (cashPaise < 0n) return "Cash received cannot be negative.";
    if (
      isForeignInvoice &&
      foreignGrossPaise !== null &&
      settlementNetPaise > foreignGrossPaise - tdsPaise
    ) {
      return "Settlement lines cannot exceed gross realized INR.";
    }
    if (parsedSettlementLines.some((line) => line.error)) {
      return parsedSettlementLines.find((line) => line.error)?.error ?? "Check settlement lines.";
    }
    if (appliedPaise <= 0n) return "Applied amount must be greater than zero.";
    if (appliedPaise > outstandingPaise) {
      return `Applied amount ${formatRupees(appliedPaise)} exceeds outstanding ${formatRupees(outstandingPaise)}.`;
    }
    return null;
  }, [
    appliedPaise,
    cashPaise,
    foreignGrossPaise,
    foreignPaidMinor,
    isForeignInvoice,
    parsedSettlementLines,
    receiptFxRatePaise,
    settlementNetPaise,
    tdsPaise,
    outstandingPaise,
  ]);

  const record = trpc.receipts.record.useMutation({
    onSuccess: () => {
      onRecorded();
      onOpenChange(false);
    },
  });

  const handleSubmit = () => {
    if (validationError) return;
    // ADR-0019: assemble settlement-line wire payload with parent_client_id
    // for bank_charge_gst rows. We auto-attach each GST line to the most
    // recent indian_bank_charge line above it.
    const wireLines = parsedSettlementLines.map((line) => line.toWire());
    const linesWithIds = wireLines.map((l, i) => ({ ...l, clientId: `cl-${i}` }));
    let lastIndianChargeClientId: string | null = null;
    const linesWithParents = linesWithIds.map((l) => {
      if (l.kind === "indian_bank_charge") {
        lastIndianChargeClientId = l.clientId;
        return l;
      }
      if (l.kind === "bank_charge_gst" && lastIndianChargeClientId) {
        return { ...l, parentClientId: lastIndianChargeClientId };
      }
      return l;
    });
    record.mutate({
      customerId: invoice.customerId,
      receivedOn,
      amountPaise: cashPaise.toString(),
      tdsPaise: tdsPaise > 0n ? tdsPaise.toString() : undefined,
      // ADR-0019: realised FX rate. Operator enters it as `receiptFxRate`
      // for foreign invoices. The domain validates this is set iff
      // hasForeignInvoice is true.
      ...(isForeignInvoice && receiptFxRatePaise > 0n
        ? { settlementRatePaise: receiptFxRatePaise.toString() }
        : {}),
      mode,
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(bankAccountId ? { bankAccountId } : {}),
      settlementLines: linesWithParents,
      allocations: [
        {
          invoiceId: invoice.id,
          paise: appliedPaise.toString(),
        },
      ],
    });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] p-0 flex flex-col max-h-[90vh] overflow-hidden">
        {/* Sticky header — never scrolls away. */}
        <div className="shrink-0 border-b border-line bg-paper px-5 py-4">
          <DialogTitle>Record receipt</DialogTitle>
          <DialogDescription className="text-sm text-muted mt-1.5">
            {customer.name} · {invoice.docNumber ?? "— draft —"} · outstanding{" "}
            <span className="font-mono">{formatRupees(outstandingPaise)}</span>
            {isForeignInvoice && invoice.foreignAmountMinor ? (
              <>
                {" "}
                ·{" "}
                <span className="font-mono">
                  {formatForeign(invoice.currencyCode, BigInt(invoice.foreignAmountMinor))}
                </span>
              </>
            ) : null}
          </DialogDescription>
        </div>

        {/* Scrollable body — the form. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field helper="Date the payment hit your bank or you got the cheque.">
              <Label htmlFor="r-received-on">Received on</Label>
              <Input
                id="r-received-on"
                type="date"
                value={receivedOn}
                onChange={(e) => setReceivedOn(e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field>
              <Label htmlFor="r-mode-trigger">Mode</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                <SelectTrigger id="r-mode-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field helper="The bank or cash account the money was received into. Credits its balance.">
            <Label htmlFor="r-account-trigger">Received into</Label>
            <Select
              value={bankAccountId}
              onValueChange={setBankAccountId}
              disabled={accounts.length === 0}
            >
              <SelectTrigger id="r-account-trigger" aria-label="Received into account">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                    {a.type === "cash" ? " · cash" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field helper="UPI txn id, NEFT UTR, cheque number, etc.">
            <Label htmlFor="r-reference">Reference (optional)</Label>
            <Input
              id="r-reference"
              type="text"
              maxLength={64}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="UPI: 4d2c…  ·  Cheque: 0046123  ·  …"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isForeignInvoice ? (
              <>
                <Field helper={`Amount the customer paid in ${invoice.currencyCode}.`}>
                  <Label htmlFor="r-foreign-paid">Customer paid ({invoice.currencyCode})</Label>
                  <Input
                    id="r-foreign-paid"
                    type="text"
                    inputMode="decimal"
                    value={foreignPaidAmount}
                    onChange={(e) => setForeignPaidAmount(sanitizeMoneyInput(e.target.value))}
                    className="font-mono"
                  />
                </Field>
                <Field helper="Rate at which this receipt was realised in INR.">
                  <Label htmlFor="r-realization-rate">Realization rate</Label>
                  <Input
                    id="r-realization-rate"
                    type="text"
                    inputMode="decimal"
                    value={receiptFxRate}
                    onChange={(e) => setReceiptFxRate(sanitizeMoneyInput(e.target.value))}
                    className="font-mono"
                  />
                </Field>
              </>
            ) : (
              <Field helper="Actual amount credited to your bank after customer-side deductions.">
                <Label htmlFor="r-cash">Bank credited (₹)</Label>
                <Input
                  id="r-cash"
                  type="text"
                  inputMode="decimal"
                  value={cashRupees}
                  onChange={(e) => setCashRupees(sanitizeMoneyInput(e.target.value))}
                  className="font-mono"
                />
              </Field>
            )}
            <Field
              helper={
                tdsPaise === 0n
                  ? customer.isTdsApplicable
                    ? "No TDS for this receipt. Click to enter an amount if customer deducted."
                    : "Customer is not flagged TDS-applicable."
                  : customer.tdsSection
                    ? `Pre-filled per ${customer.tdsSection}. Editable.`
                    : "Editable."
              }
            >
              <Label htmlFor="r-tds">
                TDS deducted (₹)
                {/* ADR-0019 — explicit "No TDS" affordance solves the zero-TDS gap */}
                {tdsPaise > 0n ? (
                  <button
                    type="button"
                    onClick={() => setTdsRupees("0")}
                    className="ml-2 text-xs font-normal text-sky-700 hover:underline"
                  >
                    Clear (no TDS)
                  </button>
                ) : null}
              </Label>
              <Input
                id="r-tds"
                type="text"
                inputMode="decimal"
                value={tdsRupees}
                onChange={(e) => setTdsRupees(sanitizeMoneyInput(e.target.value))}
                placeholder={customer.isTdsApplicable ? "0" : "No TDS expected"}
                className="font-mono"
              />
            </Field>
          </div>

          <section className="rounded-md border border-line bg-paper p-3.5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-ink">Settlement lines</h3>
                <p className="text-xs text-muted">
                  Bank charges, GST on bank charges, round-off, or custom additions.
                </p>
              </div>
              <Button
                intent="ghost"
                size="sm"
                onClick={() =>
                  setSettlementLines((lines) => [
                    ...lines,
                    newSettlementLine(
                      isForeignInvoice ? invoice.currencyCode : "INR",
                      receiptFxRate,
                    ),
                  ])
                }
              >
                <PlusIcon className="h-4 w-4" aria-hidden />
                Add line
              </Button>
            </div>

            {settlementLines.length > 0 ? (
              <div className="mt-3">
                <div className="hidden sm:grid grid-cols-[minmax(150px,1.4fr)_112px_176px_28px] gap-2 pb-1.5 text-xs font-medium uppercase tracking-wide text-muted">
                  <span>Type</span>
                  <span>Effect</span>
                  <span>Amount</span>
                  <span className="sr-only">Remove</span>
                </div>
                <div className="divide-y divide-line">
                  {settlementLines.map((line) => (
                    <SettlementLineEditor
                      key={line.id}
                      line={line}
                      invoiceCurrencyCode={invoice.currencyCode}
                      onChange={(patch) =>
                        setSettlementLines((lines) =>
                          lines.map((item) => (item.id === line.id ? { ...item, ...patch } : item)),
                        )
                      }
                      onRemove={() =>
                        setSettlementLines((lines) => lines.filter((item) => item.id !== line.id))
                      }
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          {/* /scrollable body */}
        </div>

        {/* Sticky footer — alerts + totals + buttons. Always visible so the
            operator sees the live impact while filling form fields above. */}
        <div className="shrink-0 border-t border-line bg-paper">
          {/* Inline alerts — pinned at the top of the footer so a save block
              is the first thing the operator reads when scanning the strip. */}
          {validationError || record.error ? (
            <div className="px-5 pt-3 pb-2 flex flex-col gap-2 border-b border-line">
              {validationError ? <Alert intent="warn" description={validationError} /> : null}
              {record.error ? (
                <Alert
                  intent="bad"
                  title="Couldn't save receipt"
                  description={record.error.message}
                />
              ) : null}
            </div>
          ) : null}

          {/* Totals strip — invoice-footer style: 2-col breakdown, divider,
              then the "Applied" + "Remaining" grand-total rows full-width. */}
          <div className="bg-raised/60 px-5 py-3 text-xs border-b border-line">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-muted">
              <div className="flex items-center justify-between">
                <span>Cash to bank</span>
                <span className="font-mono text-ink-2">{formatRupees(cashPaise)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>TDS deducted</span>
                <span className="font-mono">{formatRupees(tdsPaise)}</span>
              </div>
              {settlementDeductionPaise > 0n ? (
                <div className="flex items-center justify-between">
                  <span>Settlement deductions</span>
                  <span className="font-mono">{formatRupees(settlementDeductionPaise)}</span>
                </div>
              ) : null}
              {settlementAdditionPaise > 0n ? (
                <div className="flex items-center justify-between">
                  <span>Settlement additions</span>
                  <span className="font-mono">-{formatRupees(settlementAdditionPaise)}</span>
                </div>
              ) : null}
              {isForeignInvoice ? (
                <>
                  <div className="flex items-center justify-between">
                    <span>Gross realized</span>
                    <span className="font-mono">
                      {foreignGrossPaise === null ? "—" : formatRupees(foreignGrossPaise)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Gross (pre-FX)</span>
                    <span className="font-mono">{formatRupees(grossSettlementPaise)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>FX {fxGainLossPaise >= 0n ? "gain" : "loss"}</span>
                    <span
                      className={[
                        "font-mono",
                        fxGainLossPaise >= 0n ? "text-emerald-700" : "text-rose-700",
                      ].join(" ")}
                    >
                      {formatRupees(fxGainLossPaise)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span>Gross settlement</span>
                  <span className="font-mono">{formatRupees(grossSettlementPaise)}</span>
                </div>
              )}
            </div>
            <div className="mt-2 pt-2 border-t border-line">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-ink">Applied to invoice</span>
                <span className="font-mono text-base font-semibold text-ink">
                  {formatRupees(appliedPaise)}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-muted">
                <span>Remaining outstanding</span>
                <span className="font-mono">{formatRupees(outstandingPaise - appliedPaise)}</span>
              </div>
            </div>
          </div>

          {/* Action buttons. */}
          <div className="px-5 py-3 flex items-center gap-2 justify-end">
            <Button intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              intent="primary"
              size="sm"
              disabled={!!validationError || record.isPending}
              onClick={handleSubmit}
            >
              {record.isPending ? "Recording…" : "Record receipt"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function SettlementLineEditor({
  line,
  invoiceCurrencyCode,
  onChange,
  onRemove,
}: {
  line: SettlementLineState;
  invoiceCurrencyCode: string;
  onChange: (patch: Partial<SettlementLineState>) => void;
  onRemove: () => void;
}) {
  const currencies = Array.from(new Set(["INR", invoiceCurrencyCode, "USD", "AED"]));
  const isPercentLine = line.amount.trim().endsWith("%");
  const isForeignLine = line.currencyCode !== "INR" && !isPercentLine;

  const inputClassName =
    "h-8 rounded-sm border border-line bg-paper px-2 text-sm shadow-none hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100";
  const selectClassName =
    "h-8 rounded-sm border border-line bg-paper px-2 text-sm shadow-none hover:border-line-2 focus:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100";

  return (
    <div className="py-2.5 first:pt-0">
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(150px,1.4fr)_112px_176px_28px] gap-2 items-start">
        <SettlementTypeInput
          value={line.label}
          inputClassName={inputClassName}
          onPick={(nextLabel) => {
            const nextDirection = directionForLabel(nextLabel) ?? line.direction;
            const nextKind = kindForLabel(nextLabel, nextDirection);
            const option = settlementOptionForLabel(nextLabel);
            const nextCurrencyCode = option
              ? settlementCurrencyForKind(nextKind, invoiceCurrencyCode)
              : line.currencyCode;
            onChange({
              label: nextLabel,
              direction: nextDirection,
              kind: nextKind,
              currencyCode: nextCurrencyCode,
              fxRate: nextCurrencyCode === "INR" ? "" : line.fxRate,
            });
          }}
        />
        <Select
          value={line.direction}
          onValueChange={(value) => {
            const direction = value as SettlementDirection;
            onChange({
              direction,
              kind: kindForLabel(line.label, direction),
            });
          }}
        >
          <SelectTrigger className={selectClassName} aria-label="Settlement line direction">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="deduction">Deduction</SelectItem>
            <SelectItem value="addition">Addition</SelectItem>
          </SelectContent>
        </Select>
        <div className="grid grid-cols-[0.8fr_1fr] gap-2">
          {isPercentLine ? (
            <div
              aria-label="Settlement line unit"
              className={`${selectClassName} flex items-center text-muted`}
            >
              Percent
            </div>
          ) : (
            <Select
              value={line.currencyCode}
              onValueChange={(value) => onChange({ currencyCode: value })}
            >
              <SelectTrigger className={selectClassName} aria-label="Settlement line currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            aria-label="Settlement line amount"
            type="text"
            inputMode="decimal"
            value={line.amount}
            onChange={(event) =>
              onChange({ amount: sanitizeSettlementAmountInput(event.target.value) })
            }
            className={`${inputClassName} text-right font-mono`}
            placeholder="0.00 or 0.5%"
          />
        </div>
        <button
          type="button"
          aria-label="Remove settlement line"
          onClick={onRemove}
          className="flex h-8 w-7 items-center justify-center rounded-sm border border-transparent text-muted hover:border-bad-100 hover:bg-bad-50 hover:text-bad"
        >
          <Trash2Icon className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {isForeignLine ? (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_minmax(0,170px)] gap-2 items-center pl-0 sm:pl-0">
          <label className="flex items-center gap-2 text-xs text-muted">
            <span className="shrink-0 text-muted">Note</span>
            <Input
              aria-label="Settlement line note"
              type="text"
              maxLength={160}
              value={line.note}
              onChange={(event) => onChange({ note: event.target.value })}
              className={`${inputClassName} flex-1`}
              placeholder="Nostro fee, Swift charge…"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted justify-end">
            <span className="shrink-0 text-muted whitespace-nowrap">FX rate to INR</span>
            <Input
              aria-label="Settlement line FX rate"
              type="text"
              inputMode="decimal"
              value={line.fxRate}
              onChange={(event) => onChange({ fxRate: sanitizeMoneyInput(event.target.value) })}
              className={`${inputClassName} w-24 text-right font-mono`}
              placeholder="91.34"
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}

function SettlementTypeInput({
  value,
  inputClassName,
  onPick,
}: {
  value: string;
  inputClassName: string;
  onPick: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const query = value.trim().toLowerCase();
  const options = SETTLEMENT_TYPE_OPTIONS.filter((option) =>
    option.label.toLowerCase().includes(query),
  );

  return (
    <div className="relative">
      <Input
        aria-label="Settlement line type"
        value={value}
        className={inputClassName}
        onFocus={() => setFocused(true)}
        onBlur={() => window.setTimeout(() => setFocused(false), 120)}
        onChange={(event) => onPick(event.target.value)}
        placeholder="Bank charge, PayPal fee..."
      />
      {focused && options.length > 0 ? (
        <div className="absolute left-0 right-0 top-9 z-50 rounded-sm border border-line bg-paper py-1 shadow-lg">
          {options.map((option) => (
            <button
              key={option.kind}
              type="button"
              className="block w-full px-2 py-1.5 text-left text-sm text-ink hover:bg-raised"
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(option.label);
                setFocused(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function newSettlementLine(currencyCode: string, fxRate = ""): SettlementLineState {
  const isForeign = currencyCode !== "INR";
  const label = isForeign ? "Foreign bank charge" : "Indian bank charge";
  const direction = directionForLabel(label) ?? "deduction";
  return {
    id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label,
    kind: kindForLabel(label, direction),
    direction,
    amount: "",
    currencyCode,
    fxRate,
    note: "",
  };
}

function directionForLabel(label: string): SettlementDirection | null {
  const option = settlementOptionForLabel(label);
  return option?.direction ?? null;
}

function kindForLabel(label: string, direction: SettlementDirection): SettlementKind {
  const option = settlementOptionForLabel(label);
  if (option && (option.kind === "round_off" || option.direction === direction)) {
    return option.kind;
  }
  return direction === "addition" ? "other_addition" : "other_deduction";
}

function settlementOptionForLabel(label: string) {
  return SETTLEMENT_TYPE_OPTIONS.find(
    (item) => item.label.toLowerCase() === label.trim().toLowerCase(),
  );
}

function settlementCurrencyForKind(kind: SettlementKind, invoiceCurrencyCode: string): string {
  return kind === "foreign_bank_charge" && invoiceCurrencyCode !== "INR"
    ? invoiceCurrencyCode
    : "INR";
}

function parseSettlementLines(
  lines: SettlementLineState[],
  options: { defaultFxRate: string; percentBasePaise: bigint },
) {
  let indianBankChargeBase = 0n;

  return lines.map((line) => {
    const parsed = parseSettlementLine(line, {
      ...options,
      gstBasePaise: indianBankChargeBase,
    });
    if (parsed.kind === "indian_bank_charge") {
      indianBankChargeBase += parsed.amountPaise;
    }
    return parsed;
  });
}

function parseSettlementLine(
  line: SettlementLineState,
  options: { defaultFxRate: string; percentBasePaise: bigint; gstBasePaise: bigint },
) {
  const label = line.label.trim();
  const kind = kindForLabel(label, line.direction);
  const currencyCode = line.currencyCode.toUpperCase();
  const isPercent = line.amount.trim().endsWith("%");
  const enteredMinor = moneyTextToMinor(line.amount.replace("%", ""));
  const fxRateToInrPaise =
    currencyCode === "INR" ? null : moneyTextToMinor(line.fxRate || options.defaultFxRate);
  const percentBasePaise =
    kind === "bank_charge_gst" ? options.gstBasePaise : options.percentBasePaise;
  const amountPaise = isPercent
    ? (percentBasePaise * enteredMinor) / 10000n
    : currencyCode === "INR"
      ? enteredMinor
      : (enteredMinor * (fxRateToInrPaise ?? 0n)) / 100n;
  const error =
    label.length === 0
      ? "Settlement line type is required."
      : enteredMinor <= 0n
        ? "Settlement line amount must be greater than zero."
        : isPercent && percentBasePaise <= 0n
          ? "Percentage settlement lines need a positive base amount."
          : currencyCode !== "INR" && !isPercent && (!fxRateToInrPaise || fxRateToInrPaise <= 0n)
            ? "Foreign settlement lines require an FX rate."
            : null;

  return {
    kind,
    direction: line.direction,
    amountPaise,
    error,
    toWire: () => ({
      kind,
      direction: line.direction,
      label,
      amountPaise: amountPaise.toString(),
      currencyCode: isPercent ? "INR" : currencyCode,
      ...(!isPercent && currencyCode !== "INR"
        ? {
            foreignAmountMinor: enteredMinor.toString(),
            fxRateToInrPaise: fxRateToInrPaise?.toString() ?? "0",
          }
        : {}),
      ...(line.note.trim() || isPercent
        ? {
            note: [
              line.note.trim(),
              isPercent ? `${line.amount.trim()} of ${formatRupees(percentBasePaise)}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
          }
        : {}),
    }),
  };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function sanitizeMoneyInput(text: string): string {
  const cleaned = text.replace(/[^\d.]/g, "");
  const [head, ...tail] = cleaned.split(".");
  const paise = tail.join("").slice(0, 2);
  const whole = head ?? "";
  return tail.length > 0 ? `${whole}.${paise}` : whole;
}

function sanitizeSettlementAmountInput(text: string): string {
  const hasPercent = text.includes("%");
  return `${sanitizeMoneyInput(text)}${hasPercent ? "%" : ""}`;
}

/** Decimal money text → minor units. Returns 0n on empty / invalid. */
function moneyTextToMinor(text: string): bigint {
  const cleaned = text.replace(/[,\s]/g, "");
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned) || cleaned === "" || cleaned === ".") return 0n;
  const [wholeRaw, fractionRaw = ""] = cleaned.split(".");
  const whole = !wholeRaw ? "0" : wholeRaw;
  const fraction = fractionRaw.padEnd(2, "0").slice(0, 2);
  return BigInt(whole) * 100n + BigInt(fraction);
}

function minorToMajor(minor: bigint): string {
  const whole = minor / 100n;
  const fraction = minor % 100n;
  return fraction === 0n
    ? whole.toString()
    : `${whole.toString()}.${fraction.toString().padStart(2, "0")}`;
}

function maxBigInt(a: bigint, b: bigint): bigint {
  return a > b ? a : b;
}

function minBigInt(a: bigint, b: bigint): bigint {
  return a < b ? a : b;
}

function formatRupees(paise: bigint): string {
  const sign = paise < 0n ? "-" : "";
  const abs = paise < 0n ? -paise : paise;
  const rupees = abs / 100n;
  const remainder = abs % 100n;
  const fraction = remainder === 0n ? "" : `.${remainder.toString().padStart(2, "0")}`;
  return `${sign}₹${rupees.toLocaleString("en-IN")}${fraction}`;
}

function formatForeign(currencyCode: string, minor: bigint): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(minor) / 100);
}
